package com.downdogapp.client.views.playback

import com.downdogapp.*
import com.downdogapp.Color.Companion.BLACK
import com.downdogapp.Color.Companion.TRANSPARENT
import com.downdogapp.Cursor.POINTER
import com.downdogapp.Display.*
import com.downdogapp.FlexDirection.COLUMN_REVERSE
import com.downdogapp.FontWeight.SEMIBOLD
import com.downdogapp.JustifyContent.FLEX_START
import com.downdogapp.JustifyContent.SPACE_AROUND
import com.downdogapp.Overflow.HIDDEN
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.AUTO
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.Companion.vh
import com.downdogapp.Size.Companion.vw
import com.downdogapp.Size.INHERIT
import com.downdogapp.TextOverflow.ELLIPSIS
import com.downdogapp.WhiteSpace.NOWRAP
import com.downdogapp.client.*
import com.downdogapp.client.controllers.playback.PlaybackUtil
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.resources.Image
import com.downdogapp.client.resources.Images
import com.downdogapp.client.resources.image
import com.downdogapp.client.singleton.*
import com.downdogapp.client.singleton.CastState.CONNECTED
import com.downdogapp.client.singleton.CastState.CONNECTING
import com.downdogapp.client.widget.*
import kotlinx.browser.window
import kotlinx.dom.appendElement
import org.w3c.dom.*
import org.w3c.dom.events.MouseEvent
import kotlin.math.roundToInt

actual class PlaybackView actual constructor(val controller: PlaybackViewController) : TopLevelView(), View {

  private var lastMouseMove = currentClockTime()
  private var mouseInMenu = false
  private var timelineTimer: Timer

  private var videoView: HTMLDivElement
  private var readyOverlay: HTMLDivElement?

  private var castIcon: CastButton
  private var mirrorIcon: HTMLImageElement?
  private var closedCaptionsIcon: HTMLImageElement?
  private var volumeIcon: HTMLImageElement?
  private var gearIcon: HTMLImageElement
  private var fullscreenIcon: HTMLImageElement

  private var exitPracticeContainer: HTMLDivElement
  private var menuContainer: HTMLDivElement
  private var timelineContainer: HTMLDivElement
  private var poseImagesContainer: HTMLDivElement
  private var poseImages: List<PoseImageView>

  private var menuBackground: HTMLDivElement

  private var castingLabel: HTMLButtonElement
  private var currentTimeLabel: HTMLElement
  private var volumeMenu: VolumeMenuView? = null
  private var playButton: HTMLImageElement
  private var timeline: TimelineSlider
  private lateinit var settingsMenu: SettingsMenuView
  private var skipButton: HTMLImageElement
  private var likePoseButton: HTMLImageElement
  private var dislikePoseButton: HTMLImageElement
  private var poseConfirmationView: PoseConfirmationView

  private var playing: Boolean = false
    set(value) {
      playButton.image = if (value) Images.PAUSE_ICON else Images.PLAY_ICON
      field = value
    }

  private var displayTime = Duration.ZERO
    set(value) {
      val minutes = value.minutes.toInt()
      val seconds = (value - minutes(minutes)).seconds.toInt()
      val secondsString = if (seconds < 10) "0$seconds" else "$seconds"
      currentTimeLabel.text = "$minutes:$secondsString / ${controller.sequenceLength.minutes.roundToInt()}:00"
      field = value
    }

  private var seeking: Boolean = false

  actual fun updateTimelineVisibility() {}

  actual fun updatePoseImageTimelineVisibility() {
    if (controller.showPosesOnTimeline) {
      controller.isScrollingImagesManually = false
      poseImagesContainer.display = FLEX
      controller.scrollPoseImageTimeline(displayTime)
    } else {
      poseImagesContainer.display = NONE
    }
    repositionButtons()
  }

  actual fun onTimeChanged(time: Duration) {
    displayTime = time
    if (!seeking) {
      timeline.value = time.seconds
    }
  }

  actual fun pause() {
    playing = false
  }

  actual fun play() {
    playing = true
  }

  actual fun onViewLoaded() {
    settingsMenu.onViewLoaded()
    repositionButtons(menuShowing = true)
  }

  actual fun onCastStateChanged(old: CastState, new: CastState) {
    castIcon.updateImage()
    volumeMenu?.updateAudioBalance()
    if (Cast.isStarting(old, new)) {
      fullscreenIcon.hide()
      mirrorIcon?.hide()
      closedCaptionsIcon?.hide()
      castingLabel.text = Strings.CONNECTING_TO_CHROMECAST
      showControls()
      readyOverlay?.remove()
    } else if (old == CONNECTING && new == CONNECTED) {
      castingLabel.text = Strings.CASTING_TO_DEVICE(Cast.receiverName!!)
    } else if (Cast.isEnding(old, new)) {
      fullscreenIcon.show() // controls always already being shown when connecting or connected
      mirrorIcon?.show()
      closedCaptionsIcon?.show()
      castingLabel.text = ""
    }
  }

  actual fun addVideoPlayer(player: MediaPlayer) {
    (player as JsMediaPlayer).addToView(videoView)
  }

  actual fun removeVideoPlayer(onExit: Boolean) {}

  actual override fun refreshView() {
    if (controller.showSkipButton) {
      skipButton.show()
    } else {
      skipButton.hide()
    }
    PlaybackUtil.mirrorVideo.let { mirrorVideo ->
      poseImages.map { it.setMirrorImage(mirrorVideo) }
    }
    closedCaptionsIcon?.image =
      if (PlaybackUtil.showSubtitles)
        Images.CLOSED_CAPTIONS_ON_ICON
      else
        Images.CLOSED_CAPTIONS_ICON
    if (PlaybackUtil.showLikeAndExcludeButtons && controller.currentPose?.isLikeable == true) {
      likePoseButton.image =
        if (controller.currentPoseLiked == true) Images.THUMBS_UP_SELECTED else Images.THUMBS_UP
      dislikePoseButton.image =
        if (controller.currentPoseLiked == false) Images.THUMBS_DOWN_SELECTED else Images.THUMBS_DOWN

      likePoseButton.show()
      dislikePoseButton.show()
    } else {
      likePoseButton.hide()
      dislikePoseButton.hide()
    }
  }

  actual fun updateVideoFrame() {}

  actual fun showVideo() {
    videoView.show()
  }

  actual fun hideVideo() {
    videoView.hide()
  }

  actual fun showPoseConfirmation(text: String, image: Image) {
    poseConfirmationView.setText(text)
    poseConfirmationView.setImage(image)
    poseConfirmationView.fadeIn(seconds(0.3))
  }

  actual fun hidePoseConfirmation() {
    poseConfirmationView.fadeOut(seconds(0.3))
  }

  private fun showControls() {
    if ((exitPracticeContainer.opacity ?: 1.0) < 1.0) {
      exitPracticeContainer.fadeIn(FADE_TIME)
      if (!PlaybackUtil.keepTimelineVisible) {
        controller.isScrollingImagesManually = false
        timelineContainer.fadeIn(FADE_TIME)
        menuContainer.fadeIn(FADE_TIME)
        menuBackground.fadeIn(FADE_TIME)
        if (controller.showPosesOnTimeline) {
          poseImagesContainer.fadeIn(FADE_TIME)
        }
        repositionButtons(menuShowing = true)
      }
    }
  }

  private fun hideControls() {
    if ((exitPracticeContainer.opacity ?: 1.0) > 0.0) {
      exitPracticeContainer.fadeOut(FADE_TIME)
      if (!Cast.isCasting) {
        volumeMenu?.fadeOut(FADE_TIME)
        settingsMenu.fadeOut(FADE_TIME)
        if (!PlaybackUtil.keepTimelineVisible) {
          timelineContainer.fadeOut(FADE_TIME)
          menuContainer.fadeOut(FADE_TIME)
          menuBackground.fadeOut(FADE_TIME)
          if (controller.showPosesOnTimeline) {
            poseImagesContainer.fadeOut(FADE_TIME)
          }
          repositionButtons(menuShowing = false)
        }
      }
    }
  }

  override fun onResize() {
    volumeMenu?.reposition()
    settingsMenu.reposition()
    repositionButtons()
  }

  private fun repositionButtons(menuShowing: Boolean? = null) {
    val screenWidth = window.innerWidth
    val screenHeight = window.innerHeight
    val (videoWidth, videoHeight) = getVideoWidthAndHeight(
      screenWidth = screenWidth,
      screenHeight = screenHeight,
      videoWidthToHeight = controller.sequence.videoWidthToHeight
    )
    val skipButtonRight = videoHeight * controller.sequence.skipButtonRightToHeight
    val skipButtonTop = videoHeight * controller.sequence.skipButtonTopToHeight
    // unlike on the other platforms, the skip button is positioned relative to the whole window and not
    // the frame actually showing video
    skipButton.right = px(skipButtonRight + maxOf((screenWidth - videoWidth) / 2, 0))
    skipButton.top = px(skipButtonTop + maxOf((screenHeight - videoHeight) / 2, 0))
    val size = (videoHeight * controller.sequence.skipButtonSizeToHeight).toInt()
    skipButton.width = size
    skipButton.height = size
    likePoseButton.width = size
    likePoseButton.height = size
    dislikePoseButton.width = size
    dislikePoseButton.height = size
    val likePoseLeft = 10 + maxOf((screenWidth - videoWidth) / 2, 0)
    likePoseButton.left = px(likePoseLeft)
    dislikePoseButton.left = px(likePoseLeft + size)

    // Depending on screen size, pose images can overlap like buttton, so we make sure it stays above them
    val menuHeight = MENU_BACKGROUND_HEIGHT + if (controller.showPosesOnTimeline) FOCUSED_HORIZONTAL_CELL_HEIGHT else 0
    val likeButtonBottom = px(
      10 + maxOf(
        (screenHeight - videoHeight) / 2,
        when (menuShowing) {
          true -> menuHeight
          false -> 0
          null -> if (PlaybackUtil.keepTimelineVisible || (menuBackground.opacity ?: 0.0) > 0.0) menuHeight else 0
        }
      )
    )
    likePoseButton.bottom = likeButtonBottom
    dislikePoseButton.bottom = likeButtonBottom
  }

  override fun onMouseMove() {
    lastMouseMove = currentClockTime()
    showControls()
  }

  override fun onKeyPress(key: KeyStroke?) {
    when (key) {
      KeyStroke.SPACE -> {
        controller.togglePlaying()
        hideReadyOverlay()
      }

      KeyStroke.LEFT -> controller.stepBackward()
      KeyStroke.RIGHT -> controller.stepForward()
      else -> {}
    }
  }

  private fun toggleFullscreen() {
    if (Browser.fullscreen) {
      Browser.exitFullscreen()
    } else {
      Browser.requestFullscreen()
    }
  }

  private fun hideReadyOverlay() { // can safely be called mutliple times
    readyOverlay?.fadeOut(FADE_TIME) {
      readyOverlay?.removeFromParentNode()
    }
  }

  actual fun scrollPoseImageTimeline(focusedImageIndex: Int, displayTime: Duration) {
    if (!controller.isScrollingImagesManually) {
      val windowWidth = Browser.windowWidth.toDouble() // extra padding around pose images (equal to size of screen)
      val focusedImageXCoord = focusedImageIndex * UNFOCUSED_HORIZONTAL_CELL_WIDTH +
                               FOCUSED_HORIZONTAL_CELL_WIDTH / 2 -
                               MENU_HORIZONTAL_PADDING.toDouble()
      val timelineWidth = windowWidth - 2 * MENU_HORIZONTAL_PADDING.toDouble()
      val timelineThumbXCoord = displayTime / controller.sequenceLength * timelineWidth
      val scrollToXCoord = (focusedImageXCoord - timelineThumbXCoord)
                             .coerceIn(
                               focusedImageIndex * UNFOCUSED_HORIZONTAL_CELL_WIDTH + FOCUSED_HORIZONTAL_CELL_WIDTH - windowWidth,
                               (focusedImageIndex * UNFOCUSED_HORIZONTAL_CELL_WIDTH).toDouble()
                             ) + windowWidth
      poseImagesContainer.scrollTo(scrollToXCoord, 0.0)
    }

    poseImages.forEachIndexed { index, poseImage ->
      if (index == focusedImageIndex) {
        poseImage.onFocus()
      } else {
        poseImage.onUnfocus()
      }
    }
  }

  private fun HTMLDivElement.snapBack(containerAndMouseDownXCoord: Pair<Double, Int>?, mouseDownEvent: MouseEvent) {
    val minScroll = Browser.windowWidth.toDouble()
    val maxScroll = (controller.sequence.poses.size - 1) * UNFOCUSED_HORIZONTAL_CELL_WIDTH +
                    FOCUSED_HORIZONTAL_CELL_WIDTH.toDouble()
    containerAndMouseDownXCoord?.let { (containerXCoord, mouseDownXCoord) ->
      val releasedXCoord = mouseDownXCoord - mouseDownEvent.clientX + containerXCoord
      if (releasedXCoord < minScroll && releasedXCoord < maxScroll) {
        if (minScroll > maxScroll) { // If the length of the pose images is less than the screen width
          animatedScroll(releasedXCoord, maxScroll)
        } else {
          animatedScroll(releasedXCoord, minScroll)
        }
      } else if (releasedXCoord > maxScroll && releasedXCoord > minScroll) {
        if (minScroll > maxScroll) {
          animatedScroll(releasedXCoord, minScroll)
        } else {
          animatedScroll(releasedXCoord, maxScroll)
        }
      }
    }
  }

  private fun HTMLDivElement.animatedScroll(from: Double, to: Double) {
    Animation.animate(
      animationLength = seconds(0.2),
      interpolation = Interpolation.SQUARE_ROOT,
      onStep = {
        scrollTo(from + (to - from) * it, 0.0)
        true
      }
    )
  }

  override val root =
    controllerView {
      bg = background(image = null) {
        bgColor = BLACK

        centerInParent {
          overflowX = HIDDEN
          overflowY = HIDDEN

          videoView = div {
            position = RELATIVE
            width = vw(100)
            height = vh(100)
          }
        }
      }

      fg = foreground {
        overflowY = HIDDEN

        centerInParent {
          castingLabel = textButton(fontSize = 28, clickFn = null)
        }

        div {
          bgColor = TRANSPARENT
          width = percent(100)
          height = percent(100)
          position = ABSOLUTE
          top = px(0)
          left = px(0)

          onClick {
            if (volumeMenu?.visible == true || settingsMenu.visible) {
              volumeMenu?.fadeOut(FADE_TIME)
              settingsMenu.fadeOut(FADE_TIME)
            } else {
              controller.togglePlaying()
            }
          }
        }

        skipButton = imageButton(
          Images.SKIP_ICON,
          clickFn = controller::onSkipSongClicked
        ) {
          position = ABSOLUTE
        }

        likePoseButton = imageButton(
          image = Images.THUMBS_UP,
          clickFn = controller::onLikePoseClicked
        ) {
          position = ABSOLUTE
        }

        dislikePoseButton = imageButton(
          image = Images.THUMBS_DOWN,
          clickFn = controller::onDislikePoseClicked
        ) {
          position = ABSOLUTE
        }

        poseConfirmationView = poseConfirmationView {
          position = ABSOLUTE
          left = percent(50)
          bottom = px(280)
          style.transform = "translateX(-50%)"
        }


        div {
          position = ABSOLUTE
          top = px(12)
          left = px(10)

          // Exit Practice Arrow and Label
          exitPracticeContainer = horizontalFlexBox(verticalAlign = AlignItems.CENTER) {
            imageButton(Images.LEFT_ARROW_ICON, controller::onBackClicked)
            textButton(text = Strings.EXIT, fontSize = 18, clickFn = controller::onBackClicked)
          }
        }

        menuBackground = div {
          position = ABSOLUTE
          left = px(0)
          bottom = px(0)
          width = vw(100)
          height = px(MENU_BACKGROUND_HEIGHT)
          bgColor = Color.black(0.9)
        }

        poseImagesContainer = div {
          display = if (controller.showPosesOnTimeline) FLEX else NONE
          position = ABSOLUTE
          height = px(FOCUSED_HORIZONTAL_CELL_HEIGHT + 1)
          overflowX = HIDDEN
          bottom = px(MENU_BACKGROUND_HEIGHT)

          // Extra width prevents flickering on chrome
          variableWindowWidth { windowWidth ->
            width = windowWidth + px(2 * FOCUSED_HORIZONTAL_CELL_WIDTH)
          }
          left = px(-FOCUSED_HORIZONTAL_CELL_WIDTH)

          // Extra space required so that we can scroll past the first image
          div {
            variableWindowWidth { windowWidth ->
              width = windowWidth + px(FOCUSED_HORIZONTAL_CELL_WIDTH)
              flexShrink = 0.0
            }
          }

          poseImages = controller.sequence.poses.mapIndexed { index, pose ->
            poseImageView(
              title = pose.name,
              imagePath = MANIFEST.poseImageUrlPrefix + pose.imageUrlSuffix,
              clickFn = {
                controller.isScrollingImagesManually = false
                controller.onPoseClicked(index)
              },
              iconClickFn = pose.libraryUrlSuffix?.let { libraryUrlSuffix ->
                { controller.onPoseIconClicked(MANIFEST.poseLibraryUrlPrefix + libraryUrlSuffix) }
              }
            )
          }

          // Extra space required so that we can scroll past the last image
          div {
            variableWindowWidth { windowWidth ->
              width = windowWidth + px(FOCUSED_HORIZONTAL_CELL_WIDTH)
              flexShrink = 0.0
            }
          }

          // Handle dragging pose images
          var containerAndMouseDownXCoord: Pair<Double, Int>? = null
          onmouseup = {
            snapBack(containerAndMouseDownXCoord, it)
            containerAndMouseDownXCoord = null
            null
          }
          onmouseleave = {
            snapBack(containerAndMouseDownXCoord, it)
            containerAndMouseDownXCoord = null
            null
          }
          onmousedown = {
            controller.isScrollingImagesManually = true
            it.preventDefault() // prevent dragging individual images instead of container
            onMouseMove()
            containerAndMouseDownXCoord = scrollLeft to it.clientX
            null
          }
          onmousemove = {
            onMouseMove()
            containerAndMouseDownXCoord?.let { (containerXCoord, mouseDownXCoord) ->
              val scrollToXCoord = mouseDownXCoord - it.clientX + containerXCoord
              scrollTo(scrollToXCoord, 0.0)
            }
          }
        }

        div {
          position = ABSOLUTE
          left = px(0)
          bottom = px(0)

          width = vw(100)
          display = TABLE

          div {
            display = TABLE_CELL
            width = MENU_HORIZONTAL_PADDING
          }

          div {
            display = FLEX
            flexDirection = COLUMN_REVERSE

            paddingBottom = px(10)

            menuContainer = div {
              display = TABLE
              width = percent(100)
              height = px(MENU_CONTAINER_HEIGHT)
              onmouseenter = {
                mouseInMenu = true
                null
              }
              onmouseleave = {
                mouseInMenu = false
                null
              }

              div {
                display = TABLE_CELL
                width = px(255)
                height = px(MENU_CONTAINER_HEIGHT)

                centerVertically {
                  justifyContent = FLEX_START

                  playButton = imageButton(Images.PLAY_ICON, controller::togglePlaying)

                  imageButton(Images.STEP_BACKWARD_ICON, clickFn = { controller.stepBackward() })
                  imageButton(Images.STEP_FORWARD_ICON, clickFn = { controller.stepForward() })

                  currentTimeLabel = label(fontSize = 16)
                }
              }

              div() // Placeholder for empty space

              div {
                display = TABLE_CELL
                width = px(130)
                height = px(MENU_CONTAINER_HEIGHT)

                centerVertically {
                  justifyContent = SPACE_AROUND

                  if (controller.showFeedbackButton) {
                    imageButton(
                      image = Images.FEEDBACK,
                      clickFn = controller::sendFeedbackClicked
                    )
                  }

                  val googleCastLauncher =
                    (appendElement("google-cast-launcher") {} as HTMLElement).apply {
                      display = NONE
                    }

                  castIcon = castButton(clickFn = googleCastLauncher::click)
                  closedCaptionsIcon =
                    if (controller.supportsClosedCaptions)
                      imageButton(
                        image = if (PlaybackUtil.showSubtitles)
                          Images.CLOSED_CAPTIONS_ON_ICON
                        else
                          Images.CLOSED_CAPTIONS_ICON,
                        clickFn = { controller.setShowClosedCaptions(!PlaybackUtil.showSubtitles) }
                      )
                    else
                      null
                  mirrorIcon =
                    imageButton(
                      image = Images.MIRROR_ICON,
                      clickFn = { controller.setMirrorVideo(!PlaybackUtil.mirrorVideo) }
                    )
                  volumeIcon =
                    if (controller.supportsAudioBalance)
                      imageButton(
                        image = Images.VOLUME_ICON,
                        clickFn = {
                          Logger.logEvent("opened_volume_settings")
                          if (settingsMenu.visible) {
                            settingsMenu.fadeOut(FADE_TIME)
                          }

                          if (volumeMenu?.visible == true) {
                            volumeMenu?.fadeOut(FADE_TIME)
                          } else {
                            volumeMenu?.fadeIn(FADE_TIME)
                          }
                        }
                      )
                    else
                      null
                  fullscreenIcon = imageButton(Images.FULLSCREEN_ICON, ::toggleFullscreen)
                  gearIcon = imageButton(
                    image = Images.GEAR_ICON,
                    clickFn = {
                      Logger.logEvent("opened_sequence_settings")
                      if (volumeMenu?.visible == true) {
                        volumeMenu?.fadeOut(FADE_TIME)
                      }

                      if (settingsMenu.visible) {
                        settingsMenu.fadeOut(FADE_TIME)
                      } else {
                        settingsMenu.fadeIn(FADE_TIME)
                      }
                    }
                  )
                }
              }
            }

            timelineContainer = div {
              paddingBottom = px(10)
              marginTop = px(13)
              timeline = timelineSlider(
                valueRange = 0.0..controller.sequenceLength.seconds,
                onMouseDown = {
                  controller.isScrollingImagesManually = false
                  seeking = true
                  controller.onTimelinePanStarted()
                },
                onValueChanged = {
                  if (!playing) {
                    controller.isScrollingImagesManually = false
                    displayTime = seconds(this@timelineSlider.value)
                  }
                  controller.scrollPoseImageTimeline(displayTime)
                },
                onMouseUp = {
                  seeking = false
                  controller.onTimelinePanEnded(seconds(this@timelineSlider.value))
                }
              )

              onClick(cursorPointer = false) // Prevents click from being handled by background container
            }
          }

          div {
            display = TABLE_CELL
            width = MENU_HORIZONTAL_PADDING
          }
        }

        volumeMenu = volumeIcon?.let { volumeMenuView(it, controller) }

        settingsMenu = settingsMenuView(gearIcon, controller)
      }

      readyOverlay = if (AppPage.DEBUG) {
        null
      } else {
        div {
          position = ABSOLUTE
          height = INHERIT
          width = INHERIT
          bgColor = Color.black(0.6)

          onClick {
            controller.togglePlaying()
            hideReadyOverlay()
          }
          tabIndex = 1 // Ready Overlay should be tab-selectable first
          ariaLabel = Strings.TAP_TO_BEGIN

          centerInParent(blockClicks = false) {
            if (controller.sequence.readyOverlayStrings.isNotEmpty()) {
              for (string in controller.sequence.readyOverlayStrings) {
                div {
                  height = px(24)
                  label(text = string, fontSize = 18)
                }
              }
              div {
                height = px(MENU_CONTAINER_HEIGHT)
              }
            }
            centerHorizontally {
              label(
                text = Strings.TAP_TO_BEGIN,
                fontSize = 28
              )
            }
          }
        }
      }
    }

  init {
    showControls()

    timelineTimer =
      Timer(seconds(0.2)) {
        if (currentClockTime() - lastMouseMove > seconds(2) && !mouseInMenu) {
          hideControls()
        }
      }
    timelineTimer.start()
  }

  actual fun onPopped() {}

  inner class PoseImageView(title: String?, imagePath: String, clickFn: () -> Unit, iconClickFn: (() -> Unit)?) {

    private var label: HTMLSpanElement? = null
    private var image: HTMLImageElement
    private var icon: HTMLImageElement? = null
    private var isDragging = false  // Prevent triggering onClick when the user is dragging pose images

    val root = createDiv {
      position = RELATIVE
      display = FLEX
      cursor = POINTER

      image = img {
        src = imagePath
        marginTop = AUTO
        boxShadow = BoxShadow.boxShadow(
          horizontal = px(0),
          vertical = px(-1),
          color = BLACK
        )
      }

      iconClickFn?.let {
        icon = img {
          image = Images.INFO_ICON
          position = ABSOLUTE
          bottom = px(4)
          right = px(5)
          height = 15
          width = 15
          onClick {
            if (!isDragging) {
              iconClickFn()
            }
            isDragging = false
          }
        }
      }

      title?.let {
        label = label(
          text = title,
          fontSize = 12,
          fontWeight = SEMIBOLD
        ) {
          whiteSpace = NOWRAP
          overflowX = HIDDEN
          textOverflow = ELLIPSIS
          position = ABSOLUTE
          bottom = px(4)
          left = px(5)
        }
      }

      onmousedown = {
        isDragging = false
        null
      }
      onmousemove = {
        isDragging = true
        null
      }
      onClick {
        if (!isDragging) {
          clickFn()
        }
        isDragging = false
      }
    }

    fun onFocus() {
      label?.apply {
        fontSize = px(14)
        width = px(FOCUSED_HORIZONTAL_CELL_WIDTH - 25)
      }
      image.apply {
        opacity = 1.0
        width = FOCUSED_HORIZONTAL_CELL_WIDTH
        height = FOCUSED_HORIZONTAL_CELL_HEIGHT
      }
      icon?.show()
    }

    fun onUnfocus() {
      label?.apply {
        fontSize = px(12)
        width = px(UNFOCUSED_HORIZONTAL_CELL_WIDTH - 10)
      }
      image.apply {
        opacity = if (controller.isScrollingImagesManually) 0.85 else 0.7
        width = UNFOCUSED_HORIZONTAL_CELL_WIDTH
        height = UNFOCUSED_HORIZONTAL_CELL_HEIGHT
      }
      icon?.hide()
    }

    fun setMirrorImage(value: Boolean) {
      image.transform = Transform.scaleX(if (value) -1.0 else 1.0)
    }
  }

  private fun HTMLElement.poseImageView(title: String?,
                                        imagePath: String,
                                        clickFn: () -> Unit,
                                        iconClickFn: (() -> Unit)?): PoseImageView {
    val cell = PoseImageView(title, imagePath, clickFn, iconClickFn)
    appendChild(cell.root)
    return cell
  }

  private fun HTMLElement.variableWindowWidth(block: HTMLElement.(width: Size) -> Unit) {
    block(Browser.windowWidth)
    window.addEventListener(
      type = "resize",
      callback = {
        block(Browser.windowWidth)
      }
    )
  }

  companion object {

    private val MENU_HORIZONTAL_PADDING = px(20)
    private val MENU_CONTAINER_HEIGHT = 40
    private val MENU_BACKGROUND_HEIGHT = MENU_CONTAINER_HEIGHT + 53
    private val FADE_TIME = seconds(0.1)

    private val UNFOCUSED_HORIZONTAL_CELL_HEIGHT = 63
    private val UNFOCUSED_HORIZONTAL_CELL_WIDTH = 112
    private val FOCUSED_HORIZONTAL_CELL_HEIGHT = 90
    private val FOCUSED_HORIZONTAL_CELL_WIDTH = 160
  }
}
